import { Stack } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Fragment, type JSX } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { ForbiddenError, ForbiddenFallback } from './components/Fallback/GenericErrorFallback';
import { CollectionOnlyForbiddenMessage } from './constants/collection-only-messages';
import { ROUTES } from './constants/RouteNameMapping';
import { useIsCollectionOnly } from './hooks/useIsCollectionOnly';
import { useIsFundAdmin } from './hooks/useIsFundAdmin';
import { KPIRequestStatus } from './data-models/kpi-requests.data-model';
import { useKpiSourceHierarchyAvailable } from './pages/AdminPanel/KpiSources/useKpiSourceHierarchyAvailable';
import { AuditorConfigRedirect } from './pages/AdminPanel/ManageAccess/AuditorConfigRedirect';
import { useManageAccessGates } from './pages/AdminPanel/ManageAccess/useManageAccessGates';
import { useRoundCreationAvailable } from './pages/AdminPanel/RoundCreation/useRoundCreationAvailable';
import { useSourceRankingAvailable } from './pages/AdminPanel/SourceRanking/useSourceRankingAvailable';
import { getDealsRoute } from './pages/CompanyProfiles2/Deals/DealsTabRoute';
import { FrostedGate } from './components/FrostedGate/FrostedGate';
import { getShowSeparateMprRoute } from './pages/TempMpr/mprRouting';
import { RouteWrapper } from './RouteWrapper';
import { PermissionKey, PersonaKey } from './services/PermissionAndRolesKeys';
import { PermissionService } from './services/PermissionService';
import { useBeneficialOwnershipEnabled } from './pages/EntityManagement/hooks/useBeneficialOwnershipEnabled';
import { useEntityManagementEnabled } from './pages/EntityManagement/hooks/useEntityManagementEnabled';
import { clientConfigFieldsAtom } from './services/state/AppConfigStateJ';
import { isNotProduction } from './util/environment';
import { MaggieFeatureFlags } from './util/feature-flags';
import { isMobileDevice } from './util/mobile';
import { lazyPage, Preload } from './util/lazyPage';

const BulkUploadReportLoader = lazyPage(
  () => import('./pages/AdminPanel/BulkUploadReport/BulkUploadReport'),
  'BulkUploadReportLoader'
);
const UploadStatus = lazyPage(
  () => import('./pages/AdminPanel/BulkUploadReport/UploadStatus'),
  'UploadStatus'
);
const ConfigColumns = lazyPage(() => import('./pages/Config/Columns/ConfigColumns'), 'ConfigColumns');
const ConfigFormatters = lazyPage(
  () => import('./pages/Config/Formatters/ConfigFormatters'),
  'ConfigFormatters'
);
const CustomFields = lazyPage(() => import('./pages/AdminPanel/CustomFields/CustomFields'), 'CustomFields');
const FieldManager = lazyPage(() => import('./pages/AdminPanel/FieldManager/FieldManager'), 'FieldManager');
const KpiSourcesConfig = lazyPage(
  () => import('./pages/AdminPanel/KpiSources/KpiSourcesConfig'),
  'KpiSourcesConfig'
);
const SourceRankingConfig = lazyPage(
  () => import('./pages/AdminPanel/SourceRanking/SourceRankingConfig'),
  'SourceRankingConfig'
);
const AuditorDateRangesConfig = lazyPage(
  () => import('./pages/AdminPanel/AuditorDateRanges/AuditorDateRangesConfig'),
  'AuditorDateRangesConfig'
);
const RoundManagement = lazyPage(
  () => import('./pages/AdminPanel/RoundManagement/RoundManagement'),
  'RoundManagement'
);
const RoundCreationConfig = lazyPage(
  () => import('./pages/AdminPanel/RoundCreation/RoundCreationConfig'),
  'RoundCreationConfig'
);
const DataCollectionConfig = lazyPage(
  () => import('./pages/AdminPanel/DataCollectionConfig/DataCollectionConfig'),
  'DataCollectionConfig'
);
const CompanyDetailsConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/CompanyDetailsConfig'),
  'CompanyDetailsConfig'
);
const CompanyListsOverviewConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/CompanyListsOverviewConfig'),
  'CompanyListsOverviewConfig'
);
const CompanySummaryFinancialSettingsConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/CompanySummaryFinancialSettingsConfig'),
  'CompanySummaryFinancialSettingsConfig'
);
const CompanySummaryKPIChartsConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/CompanySummaryKPIChartsConfig'),
  'CompanySummaryKPIChartsConfig'
);
const CompanySummaryPerformanceSettingsConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/CompanySummaryPerformanceSettingsConfig'),
  'CompanySummaryPerformanceSettingsConfig'
);
const DataDisplay = lazyPage(() => import('./pages/AdminPanel/DataDisplay/DataDisplay'), 'DataDisplay');
const InvestmentMetricsConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/InvestmentMetricsConfig'),
  'InvestmentMetricsConfig'
);
const KpiDisplayConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/KpiDisplayConfig'),
  'KpiDisplayConfig'
);
const PortfolioKpiCardsConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/PortfolioKpiCardsConfig'),
  'PortfolioKpiCardsConfig'
);
const ListCustomization = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/ListCustomization'),
  'ListCustomization'
);
const ProfileCustomization = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/ProfileCustomization'),
  'ProfileCustomization'
);
const TearSheetCapitalizationSummaryConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetCapitalizationSummaryConfig'
);
const TearSheetCompanyDescriptionConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetCompanyDescriptionConfig'
);
const TearSheetCompanySummaryConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetCompanySummaryConfig'
);
const TearSheetConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetConfig'
);
const TearSheetFinancingHistoryConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetFinancingHistoryConfig'
);
const TearSheetHeaderConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetHeaderConfig'
);
const TearSheetInvestmentSummaryConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetInvestmentSummaryConfig'
);
const TearSheetInvestmentThesisConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetInvestmentThesisConfig'
);
const TearSheetPerformanceCommentaryConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetPerformanceCommentaryConfig'
);
const TearSheetPerformanceSummaryConfig = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearSheetConfig'),
  'TearSheetPerformanceSummaryConfig'
);
const TearsheetV2LayoutBuilder = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearsheetV2/TearsheetV2LayoutBuilder'),
  'TearsheetV2LayoutBuilder'
);
const GeneralLedgerLoader = lazyPage(
  () => import('./pages/AdminPanel/GeneralLedger/GeneralLedger'),
  'GeneralLedgerLoader'
);
const BulkDealDocsUploadLoader = lazyPage(
  () => import('./pages/AdminPanel/BulkDealDocsUpload/BulkDealDocsUpload'),
  'BulkDealDocsUploadLoader'
);
const UpdateDealDocsRoundLoader = lazyPage(
  () => import('./pages/AdminPanel/UpdateDealDocsRound/UpdateDealDocsRound'),
  'UpdateDealDocsRoundLoader'
);
const TearsheetGeneration = lazyPage(
  () => import('./pages/AdminPanel/ManageAccess/BulkTearsheetGeneration/TearsheetGeneration'),
  'TearsheetGeneration'
);
const ManageAccess = lazyPage(() => import('./pages/AdminPanel/ManageAccess/ManageAccess'), 'ManageAccess');
const ManageAccessColumnGridDisplayDataLoader = lazyPage(
  () => import('./pages/AdminPanel/ManageAccess/ManageAccessColumnGridDisplay'),
  'ManageAccessColumnGridDisplayDataLoader'
);
const ManageAccessCompanyDataLoader = lazyPage(
  () => import('./pages/AdminPanel/ManageAccess/ManageAccessCompany'),
  'ManageAccessCompanyDataLoader'
);
const ManageAccessFundAdminDataLoader = lazyPage(
  () => import('./pages/AdminPanel/ManageAccess/FundAdmin/ManageAccessFundAdmin'),
  'ManageAccessFundAdminDataLoader'
);
const CompanyMappings = lazyPage(
  () => import('./pages/AdminPanel/Mappings/CompanyMappings'),
  'CompanyMappings'
);
const OrgProfile = lazyPage(() => import('./pages/AdminPanel/OrgProfile/OrgProfile'), 'OrgProfile');
const ResetReingestion = lazyPage(
  () => import('./pages/AdminPanel/ResetReingestion/ResetReingestion'),
  'ResetReingestion'
);
const RubricsConfigLoader = lazyPage(
  () => import('./pages/AdminPanel/Rubrics/RubricsConfig'),
  'RubricsConfigLoader'
);
const TableDataDownloadLoader = lazyPage(
  () => import('./pages/AdminPanel/TableDataDownload/TableDataDownload'),
  'TableDataDownloadLoader'
);
const CaptableFormWrapper = lazyPage(
  () => import('./pages/CompanyProfiles/CapTable/CapTableForm'),
  'CaptableFormWrapper'
);
const CompanyProfileCapTable = lazyPage(
  () => import('./pages/CompanyProfiles/CapTable/CompanyProfileCapTable'),
  'CompanyProfileCapTable'
);
const Companies = lazyPage(() => import('./pages/CompanyProfiles/Companies/Companies'), 'Companies');
const CompanyListLoader = lazyPage(
  () => import('./pages/CompanyProfiles/Companies/Companies'),
  'CompanyListLoader'
);
const CompanyProfileFinancials = lazyPage(
  () => import('./pages/CompanyProfiles/components/Financials/CompanyProfileFinancials'),
  'CompanyProfileFinancials'
);
const CompanyProfilesPeople = lazyPage(
  () => import('./pages/CompanyProfiles/People/CompanyProfilesPeople'),
  'CompanyProfilesPeople'
);
const CompanyProfilePerformance = lazyPage(
  () => import('./pages/CompanyProfiles/Performance/CompanyProfilePerformance'),
  'CompanyProfilePerformance'
);
const ScenariosWrapper = lazyPage(
  () => import('./pages/CompanyProfiles/Scenarios/AllScenarios/ScenariosWrapper'),
  'ScenariosWrapper'
);
const ScenarioForm = lazyPage(
  () => import('./pages/CompanyProfiles/Scenarios/SingleScenario/form/ScenarioForm'),
  'ScenarioForm'
);
const SingleScenario = lazyPage(
  () => import('./pages/CompanyProfiles/Scenarios/SingleScenario/SingleScenario'),
  'SingleScenario'
);
const ScenarioVisualization = lazyPage(
  () => import('./pages/CompanyProfiles/Scenarios/SingleScenario/visualization/ScenarioVisualization'),
  'ScenarioVisualization'
);
const WeightedOutcomeVisualization = lazyPage(
  () => import('./pages/CompanyProfiles/Scenarios/SingleScenario/visualization/WeightedOutcomeVisualization'),
  'WeightedOutcomeVisualization'
);
const CompaniesOverview = lazyPage(
  () => import('./pages/CompanyProfiles2/CompaniesOverview'),
  'CompaniesOverview'
);
const CompanySummary2 = lazyPage(
  () => import('./pages/CompanyProfiles2/Summary/CompanySummary2'),
  'CompanySummary2'
);
const ConfigClient = lazyPage(() => import('./pages/Config/Client/ConfigClient'), 'ConfigClient');
const ConfigDealStage = lazyPage(
  () => import('./pages/Config/ConfigDealStage/ConfigDealStage'),
  'ConfigDealStage'
);
const ConfigDealType = lazyPage(
  () => import('./pages/Config/ConfigDealType/ConfigDealType'),
  'ConfigDealType'
);
const FieldAutomationConfig = lazyPage(
  () => import('./pages/Config/FieldAutomation/FieldAutomationConfig'),
  'FieldAutomationConfig'
);
const ConfigFund = lazyPage(() => import('./pages/Config/Fund/ConfigFund'), 'ConfigFund');
const ConfigKPIs = lazyPage(() => import('./pages/Config/KPI/ConfigKPI'), 'ConfigKPIs');
const ConfigKPIGroups = lazyPage(() => import('./pages/Config/KPIGroups/ConfigKPIGroups'), 'ConfigKPIGroups');
const ConfigMissReasons = lazyPage(
  () => import('./pages/Config/Reasons/ConfigMissReasons'),
  'ConfigMissReasons'
);
const ConfigPassReasons = lazyPage(
  () => import('./pages/Config/Reasons/ConfigPassReasons'),
  'ConfigPassReasons'
);
const ConfigSector = lazyPage(() => import('./pages/Config/Sector/ConfigSector'), 'ConfigSector');
const DealAnalytics = lazyPage(
  () => import('./pages/DealFlow2/components/DealAnalytics/DealAnalytics'),
  'DealAnalytics'
);
const DealAnalyticsDuration = lazyPage(
  () => import('./pages/DealFlow2/components/DealAnalytics/Details/DealAnalyticsDuration'),
  'DealAnalyticsDuration'
);
const DAOverview = lazyPage(
  () => import('./pages/DealFlow2/components/DealAnalytics/Overview/DAOverview'),
  'DAOverview'
);
const DealsView = lazyPage(() => import('./pages/DealFlow2/DealFlow'), 'DealsView');
const AllSecurities = lazyPage(() => import('./pages/Finance2/AllSecurities/AllSecurities'), 'AllSecurities');
const AllTransactions = lazyPage(
  () => import('./pages/Finance2/AllTransactions/AllTransactions'),
  'AllTransactions'
);
const ReconSection = lazyPage(() => import('./pages/Finance2/Dashboard/Recon/ReconSection'), 'ReconSection');
const RoundReconGrid = lazyPage(
  () => import('./pages/Finance2/Dashboard/Recon/RoundReconGrid'),
  'RoundReconGrid'
);
const TransactionReconGrid = lazyPage(
  () => import('./pages/Finance2/Dashboard/Recon/TransactionReconGrid'),
  'TransactionReconGrid'
);
const FinanceRounds = lazyPage(() => import('./pages/Finance2/Rounds/FinanceRounds'), 'FinanceRounds');
const LedgerGrid = lazyPage(() => import('./pages/Finance2/Security/LedgerGrid'), 'LedgerGrid');
const TransactionHistory = lazyPage(
  () => import('./pages/Finance2/Transactions/TransactionHistory'),
  'TransactionHistory'
);
const TransactionsTable3 = lazyPage(
  () => import('./pages/Finance2/Transactions3/Transactions3'),
  'TransactionsTable3'
);
const FPAllocationLoader = lazyPage(
  () => import('./pages/FundProfiles/Allocation/FPAllocation'),
  'FPAllocationLoader'
);
const FundReservesLoader = lazyPage(
  () => import('./pages/FundProfiles/Reserves/FundReserves'),
  'FundReservesLoader'
);
const FPSummary = lazyPage(() => import('./pages/FundProfiles/Summary/FPSummary'), 'FPSummary');
const FPWaterfall = lazyPage(() => import('./pages/FundProfiles/Waterfall/FPWaterfall'), 'FPWaterfall');
const AffinityConfiguration = lazyPage(
  () => import('./pages/Integrations/Affinity/AffinityConfiguration'),
  'AffinityConfiguration'
);
const AirtableSync = lazyPage(() => import('./pages/Integrations/Airtable/AirtableSync'), 'AirtableSync');
const AllVueConfiguration = lazyPage(
  () => import('./pages/Integrations/AllVue/AllVueConfiguration'),
  'AllVueConfiguration'
);
const ArkPesConfiguration = lazyPage(
  () => import('./pages/Integrations/ArkPes/ArkPesConfiguration'),
  'ArkPesConfiguration'
);
const IntegrationDetail = lazyPage(
  () => import('./pages/Integrations/IntegrationDetail'),
  'IntegrationDetail'
);
const ILevelConfiguration = lazyPage(
  () => import('./pages/Integrations/ILevel/ILevelConfiguration'),
  'ILevelConfiguration'
);
const QuickBooksConfiguration = lazyPage(
  () => import('./pages/Integrations/QuickBooks/QuickBooksConfiguration'),
  'QuickBooksConfiguration'
);
const StandardMetricsConfiguration = lazyPage(
  () => import('./pages/Integrations/StandardMetrics/StandardMetricsConfiguration'),
  'StandardMetricsConfiguration'
);
const CobaltConfiguration = lazyPage(
  () => import('./pages/Integrations/Cobalt/CobaltConfiguration'),
  'CobaltConfiguration'
);
const ConsolidatedDataCollectionConfiguration = lazyPage(
  () => import('./pages/Integrations/ConsolidatedDataCollection/ConsolidatedDataCollectionConfiguration'),
  'ConsolidatedDataCollectionConfiguration'
);
const AllCompanies = lazyPage(
  () => import('./pages/KPI/components/KPICompanies/AllCompanies'),
  'AllCompanies'
);
const AssignedCompanies = lazyPage(
  () => import('./pages/KPI/components/KPICompanies/AssignedCompanies'),
  'AssignedCompanies'
);
const KPIRecipientView3 = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/KPIRequestResponse/KPIRecipientView3'),
  'KPIRecipientView3'
);
const KPIRequestResponseHome = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/KPIRequestResponse/KPIRequestResponseHome'),
  'KPIRequestResponseHome'
);
const KPIReviewerView = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/KPIRequestResponse/KPIReviewerView'),
  'KPIReviewerView'
);
const KPIThankYouScreen = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/KPIRequestResponse/KPIThankYouScreen'),
  'KPIThankYouScreen'
);
const ManagedServiceView = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/KPIRequestResponse/ManagedServiceView'),
  'ManagedServiceView'
);
const KPIResponses = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/KPIRequestsAndResponses'),
  'KPIResponses'
);
const KPIResponsesGrid = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/KPIResponsesGrid'),
  'KPIResponsesGrid'
);
const KpiRequestManageLoader = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/Manage/KpiRequestManage'),
  'KpiRequestManageLoader'
);
const ResponseAnalysis = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/ResponseAnalysis/ResponseAnalysis'),
  'ResponseAnalysis'
);
const SentRequests = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/SentRequests'),
  'SentRequests'
);
const UnsentKpiRequestsGrid = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/UnsentKpiRequestsGrid'),
  'UnsentKpiRequestsGrid'
);
const KpiModuleGate = lazyPage(() => import('./pages/KPI/KpiModuleGate'), 'KpiModuleGate');
const PipelineModulePreview = lazyPage(
  () => import('./pages/DealFlow2/PipelineModulePreview'),
  'PipelineModulePreview'
);
const SourcingModulePreview = lazyPage(
  () => import('./pages/Sourcing/SourcingModulePreview'),
  'SourcingModulePreview'
);
const KPITemplate = lazyPage(() => import('./pages/KPI/KPITemplate'), 'KPITemplate');
const KPITemplatesRedirect = lazyPage(() => import('./pages/KPI/KPITemplate'), 'KPITemplatesRedirect');
const LoginSuccess = lazyPage(() => import('./pages/LoginSuccess/LoginSuccess'), 'LoginSuccess');
const TempMprWrapper = lazyPage(() => import('./pages/TempMpr/TempMprWrapper'), 'TempMprWrapper');
const UI = lazyPage(() => import('./pages/UI/UI'), 'UI');
const KpiCollectionConfigsPage = lazyPage(
  () => import('./pages/KPI/KpiCollection/KpiCollectionConfigsPage'),
  'KpiCollectionConfigsPage'
);
const KpiCollectionConfigsRedirect = lazyPage(
  () => import('./pages/KPI/KpiCollection/KpiCollectionConfigsPage'),
  'KpiCollectionConfigsRedirect'
);
const CustomKpiTemplatePage = lazyPage(
  () => import('./pages/KPI/KpiCollection/CustomKpiTemplate/CustomKpiTemplatePage'),
  'CustomKpiTemplatePage'
);
const CustomKpiTemplateRedirect = lazyPage(
  () => import('./pages/KPI/KpiCollection/CustomKpiTemplate/CustomKpiTemplatePage'),
  'CustomKpiTemplateRedirect'
);
const KpiCompanySettings = lazyPage(
  () => import('./pages/KPI/KpiCollection/KpiCompanySettings'),
  'KpiCompanySettings'
);
const CompanyPeriodRequestManageLoader = lazyPage(
  () => import('./pages/KPI/ManageRequests/CompanyPeriodRequestManageLoader'),
  'CompanyPeriodRequestManageLoader'
);
const CompanyPeriodRequestManageLoaderV2 = lazyPage(
  () => import('./pages/KPI/ManageRequests/CompanyPeriodRequestManageLoaderV2'),
  'CompanyPeriodRequestManageLoaderV2'
);
const TearsheetV1LayoutBuilder = lazyPage(
  () => import('./pages/AdminPanel/DataDisplay/TearsheetV1/TearsheetV1LayoutBuilder'),
  'TearsheetV1LayoutBuilder'
);
const SourcingPage = lazyPage(() => import('./pages/Sourcing/SourcingPage'), 'SourcingPage');
const SourcingListsView = lazyPage(() => import('./pages/Sourcing/SourcingListsView'), 'SourcingListsView');
const SourcingResultsPage = lazyPage(
  () => import('./pages/Sourcing/SourcingResultsPage'),
  'SourcingResultsPage'
);
const SourcingAgentQueryResultPage = lazyPage(
  () => import('./pages/Sourcing/SourcingAgentQueryResultPage'),
  'SourcingAgentQueryResultPage'
);
const SourcingProfilePage = lazyPage(
  () => import('./pages/Sourcing/SourcingProfilePage'),
  'SourcingProfilePage'
);
const KPIRequestsAndResponses = lazyPage(
  () => import('./pages/KPI/components/KPIRequests/KPIRequestsAndResponses'),
  'default'
);
const KPICompanies = lazyPage(() => import('./pages/KPI/components/KPICompanies/KPICompanies'), 'default');
const PortfolioOverview = lazyPage(() => import('./pages/PortfolioOverview/PortfolioOverview'), 'default');
const PortfolioRedirect = lazyPage(
  () => import('./pages/PortfolioOverview/components/PortfolioRedirect'),
  'PortfolioRedirect'
);
const ChatBot = lazyPage(() => import('./pages/ChatBot/ChatBot'), 'default');
const AdminPanel = lazyPage(() => import('./pages/AdminPanel/AdminPanel'), 'default');
const Finance2 = lazyPage(() => import('./pages/Finance2/Finance2'), 'default');
const EntityManagementPage = lazyPage(
  () => import('./pages/EntityManagement/EntityManagementPage'),
  'EntityManagementPage'
);
const StructureChartSurface = lazyPage(
  () => import('./pages/EntityManagement/surfaces/PlaceholderSurfaces'),
  'StructureChartSurface'
);
const RegistrySurface = lazyPage(
  () => import('./pages/EntityManagement/surfaces/PlaceholderSurfaces'),
  'RegistrySurface'
);
const DOSlateSurface = lazyPage(
  () => import('./pages/EntityManagement/surfaces/PlaceholderSurfaces'),
  'DOSlateSurface'
);
const BeneficialOwnershipSurface = lazyPage(
  () => import('./pages/EntityManagement/surfaces/PlaceholderSurfaces'),
  'BeneficialOwnershipSurface'
);
const ReviewQueuesSurface = lazyPage(
  () => import('./pages/EntityManagement/surfaces/ReviewQueues/ReviewQueuesSurface'),
  'ReviewQueuesSurface'
);
const DocumentHubPage = lazyPage(() => import('./pages/DocumentHub/DocumentHubPage'), 'DocumentHubPage');
const DocumentHub = lazyPage(() => import('./pages/DocumentHub/DocumentHub'), 'default');
const DocumentHubPreview = lazyPage(
  () => import('./pages/DocumentHub/DocumentHubPreview'),
  'DocumentHubPreview'
);
const FinanceModulePreview = lazyPage(
  () => import('./pages/Finance2/FinanceModulePreview'),
  'FinanceModulePreview'
);
const EntityManagementModulePreview = lazyPage(
  () => import('./pages/EntityManagement/EntityManagementModulePreview'),
  'EntityManagementModulePreview'
);
const PortfolioModulePreview = lazyPage(
  () => import('./pages/PortfolioOverview/PortfolioModulePreview'),
  'PortfolioModulePreview'
);
const FundProfiles = lazyPage(() => import('./pages/FundProfiles/FundProfiles'), 'default');
const DealFlowPreLoader = lazyPage(() => import('./pages/DealFlow2/DealFlow'), 'default');
const CompanyProfiles2 = lazyPage(() => import('./pages/CompanyProfiles2/CompanyProfiles2'), 'default');
const IntegrationApps = lazyPage(() => import('./pages/Integrations/IntegrationApps'), 'default');
const CartaConfiguration = lazyPage(() => import('./pages/Integrations/Carta/CartaConfiguration'), 'default');
const CartaFirmsTab = lazyPage(() => import('./pages/Integrations/Carta/tabs/FirmsTab'), 'default');
const CartaFundsTab = lazyPage(() => import('./pages/Integrations/Carta/tabs/FundsTab'), 'default');
const CartaCompaniesTab = lazyPage(() => import('./pages/Integrations/Carta/tabs/CompaniesTab'), 'default');
const CartaHoldersTab = lazyPage(() => import('./pages/Integrations/Carta/tabs/HoldersTab'), 'default');
const CartaRunsTab = lazyPage(() => import('./pages/Integrations/Carta/tabs/RunsTab'), 'default');
const Mappings = lazyPage(() => import('./pages/Mappings/Mappings'), 'default');
const ReconciliationMonitor = lazyPage(
  () => import('./pages/AdminPanel/ReconciliationMonitor/ReconciliationMonitor'),
  'default'
);
const AtlasConsole = lazyPage(() => import('./pages/AdminPanel/AtlasConsole/AtlasConsole'), 'default');
const Processing = lazyPage(() => import('./pages/AdminPanel/Processing/Processing'), 'default');
const ArgusProfileInspector = lazyPage(
  () => import('./pages/AdminPanel/ArgusProfileInspector/ArgusProfileInspector'),
  'default'
);
const ArgusProfileDetail = lazyPage(
  () => import('./pages/AdminPanel/ArgusProfileInspector/ArgusProfileDetail'),
  'default'
);

// keep the list-origin navigation state through the summary-tab redirect
function FPIndexRedirect() {
  const location = useLocation();
  return <Navigate to={ROUTES.FP_SUMMARY} replace state={location.state} />;
}

function getDataCollectionModuleRoutes(
  permSvc: PermissionService,
  usingConsolidatedDataCollection: boolean,
  useManagedServices: boolean,
  useStarbreakerCollectionV2: boolean,
  isFundAdmin: boolean
): JSX.Element[] {
  if (permSvc.hasPermission(PermissionKey.canReadDataCollection) && !isFundAdmin) {
    const consolidatedDataCollectionRoutes: JSX.Element[] = useStarbreakerCollectionV2
      ? [
          <Route key={ROUTES.KPI} path={ROUTES.KPI} element={<KpiModuleGate variant='consolidatedV2' />}>
            <Route path={ROUTES.COMPANIES} element={<KPICompanies />}>
              <Route index element={<AllCompanies />} />
              <Route path={ROUTES.ASSIGNED_COMPANIES} element={<AssignedCompanies />} />
            </Route>
            <Route path={ROUTES.KPI_CUSTOM_TEMPLATE_CONFIGURATION}>
              <Route index element={<CustomKpiTemplateRedirect />} />
              <Route path=':templateId' element={<CustomKpiTemplatePage />} />
            </Route>
            <Route path={ROUTES.KPI_MANAGE} element={<CompanyPeriodRequestManageLoaderV2 />} />
          </Route>,
        ]
      : [
          <Route key={ROUTES.KPI} path={ROUTES.KPI} element={<KpiModuleGate variant='consolidated' />}>
            <Route path={ROUTES.COMPANIES} element={<KpiCompanySettings />} />
            <Route path={ROUTES.KPI_DATA_COLLECTION_CONFIGURATION}>
              <Route index element={<KpiCollectionConfigsRedirect />} />
              <Route path=':configId' element={<KpiCollectionConfigsPage />} />
            </Route>
            <Route path={ROUTES.KPI_MANAGE} element={<CompanyPeriodRequestManageLoader />} />
          </Route>,
        ];

    const nonConsolidatedDataCollectionRoutes: JSX.Element[] = [
      <Route key={ROUTES.KPI} path={ROUTES.KPI} element={<KpiModuleGate variant='legacy' />}>
        <Route path={ROUTES.KPI_REQUESTS} key={ROUTES.KPI_REQUESTS} element={<KPIRequestsAndResponses />}>
          <Route index element={<UnsentKpiRequestsGrid />} />
          <Route
            path={ROUTES.KPI_REQUESTS_IN_PROGRESS}
            element={<SentRequests status={KPIRequestStatus.Sent} />}
          />
          <Route
            path={ROUTES.KPI_REQUESTS_ARCHIVED}
            element={<SentRequests status={KPIRequestStatus.Archived} />}
          />
        </Route>
        <Route path={ROUTES.KPI_RESPONSES} element={<KPIResponses />}>
          <Route index element={<KPIResponsesGrid status={KPIRequestStatus.Submitted} />} />
          {useManagedServices && (
            <Route
              path={ROUTES.KPI_RESPONSES_MS_REVIEWED}
              element={<KPIResponsesGrid status={KPIRequestStatus.ManagedServiceFinished} />}
            />
          )}
          <Route path={ROUTES.KPI_RESPONSES_ACCEPTED} element={<Outlet />}>
            <Route index element={<KPIResponsesGrid status={KPIRequestStatus.Accepted} />} />
            <Route path={ROUTES.KPI_RESPONSES_ANALYSIS} element={<ResponseAnalysis />} />
          </Route>
          <Route
            path={ROUTES.KPI_RESPONSES_REJECTED}
            element={<KPIResponsesGrid status={KPIRequestStatus.Rejected} />}
          />
        </Route>

        <Route path={ROUTES.COMPANIES} element={<KPICompanies />}>
          <Route index key={ROUTES.COMPANIES} element={<AllCompanies />} />
          <Route path={ROUTES.ASSIGNED_COMPANIES} element={<AssignedCompanies />} />
        </Route>

        <Route path={ROUTES.KPI_TEMPLATES}>
          <Route index element={<KPITemplatesRedirect />} />
          <Route path=':templateId' element={<KPITemplate />} />
        </Route>

        <Route path={ROUTES.KPI_MANAGE} element={<KpiRequestManageLoader />} />
      </Route>,
    ];

    const result = usingConsolidatedDataCollection
      ? consolidatedDataCollectionRoutes
      : nonConsolidatedDataCollectionRoutes;

    if (permSvc.hasPermission(PermissionKey.canSubmitCollectionSubmit)) {
      result.push(
        <Fragment key={'data-collection-form-routes'}>
          <Route
            key={ROUTES.KPI_THANK_YOU_SCREEN}
            path={ROUTES.KPI_THANK_YOU_SCREEN}
            element={
              <Stack width='100%' height='100%'>
                <KPIThankYouScreen />
              </Stack>
            }
          />
          <Route
            key={ROUTES.KPI_PENDING_REQUESTS}
            path={ROUTES.KPI_PENDING_REQUESTS}
            element={
              <Stack width='100%' height='100%'>
                <KPIRecipientView3 />
              </Stack>
            }
          />
        </Fragment>
      );
    }

    return result;
  } else {
    return [<Route path={`${ROUTES.KPI}/*`} element={<RouteNotAllowed />} key={ROUTES.KPI} />];
  }
}

/**
 * Get Company Profile routes for users with auditor access
 * Auditors only have access to Summary, Financials and, with the Deals tab permission, Deals
 */
function getCompanyProfileAuditorRoutes(
  permSvc: PermissionService,
  showPeopleReporting: boolean
): JSX.Element[] {
  return [
    // Summary route
    permSvc.hasPermission(PermissionKey.canViewMetrics) ? (
      <Route key='summary' path={ROUTES.COMPANY_PROFILE_SUMMARY} element={<CompanySummary2 />} />
    ) : (
      <Route
        key='summary-blocked'
        path={`${ROUTES.COMPANY_PROFILE_SUMMARY}/*`}
        element={<RouteNotAllowed />}
      />
    ),

    // Financials route
    permSvc.hasPermission(PermissionKey.canViewCompanyFinancials) ? (
      <Route
        key='financials'
        path={ROUTES.COMPANY_PROFILE_FINANCIALS}
        element={<CompanyProfileFinancials />}
      />
    ) : (
      <Route
        key='financials-blocked'
        path={`${ROUTES.COMPANY_PROFILE_FINANCIALS}/*`}
        element={<RouteNotAllowed />}
      />
    ),

    // Block all other routes for auditors
    <Route
      key='performance-blocked'
      path={`${ROUTES.COMPANY_PROFILE_PERFORMANCE}/*`}
      element={<RouteNotAllowed />}
    />,
    ...(showPeopleReporting
      ? [
          <Route
            key='people-blocked'
            path={`${ROUTES.COMPANY_PROFILE_PEOPLE}/*`}
            element={<RouteNotAllowed />}
          />,
        ]
      : []),
    <Route
      key='scenarios-blocked'
      path={`${ROUTES.COMPANY_PROFILE_SCENARIOS}/*`}
      element={<RouteNotAllowed />}
    />,
    <Route
      key='captable-blocked'
      path={`${ROUTES.COMPANY_PROFILE_CAP_TABLE}/*`}
      element={<RouteNotAllowed />}
    />,
    getDealsRoute(permSvc, <RouteNotAllowed />),
    <Route
      key='mappings-blocked'
      path={`${ROUTES.COMPANY_PROFILE_MAPPINGS}/*`}
      element={<RouteNotAllowed />}
    />,
  ];
}

/** A fund admin's profile is Summary and Deals; the API closes the routes behind the other tabs (FD-5905). */
function getCompanyProfileFundAdminRoutes(permSvc: PermissionService): JSX.Element[] {
  return [
    permSvc.hasPermission(PermissionKey.canViewMetrics) ? (
      <Route key='summary' path={ROUTES.COMPANY_PROFILE_SUMMARY} element={<CompanySummary2 />} />
    ) : (
      <Route
        key='summary-blocked'
        path={`${ROUTES.COMPANY_PROFILE_SUMMARY}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    getDealsRoute(permSvc, <RouteNotAllowed />),
    ...[
      ROUTES.COMPANY_PROFILE_PERFORMANCE,
      ROUTES.COMPANY_PROFILE_FINANCIALS,
      ROUTES.COMPANY_PROFILE_PEOPLE,
      ROUTES.COMPANY_PROFILE_SCENARIOS,
      ROUTES.COMPANY_PROFILE_CAP_TABLE,
      ROUTES.COMPANY_PROFILE_MAPPINGS,
    ].map((path) => <Route key={`${path}-blocked`} path={`${path}/*`} element={<RouteNotAllowed />} />),
  ];
}

/**
 * Get standard Company Profile routes for non-auditor users
 */
function getCompanyProfileStandardRoutes(
  permSvc: PermissionService,
  showPeopleReporting: boolean,
  showWeightedScenarios: boolean
): JSX.Element[] {
  const routes: JSX.Element[] = [
    permSvc.hasPermission(PermissionKey.canViewMetrics) ? (
      <Route key='summary' path={ROUTES.COMPANY_PROFILE_SUMMARY} element={<CompanySummary2 />} />
    ) : (
      <Route
        key='summary-blocked'
        path={`${ROUTES.COMPANY_PROFILE_SUMMARY}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    permSvc.hasPermission(PermissionKey.canViewKpis) ? (
      <Route
        key='performance'
        path={ROUTES.COMPANY_PROFILE_PERFORMANCE}
        element={<CompanyProfilePerformance />}
      />
    ) : (
      <Route
        key='performance-blocked'
        path={`${ROUTES.COMPANY_PROFILE_PERFORMANCE}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    ...(showPeopleReporting
      ? [<Route key='people' path={ROUTES.COMPANY_PROFILE_PEOPLE} element={<CompanyProfilesPeople />} />]
      : []),
  ];

  // Add remaining routes
  routes.push(
    permSvc.hasPermission(PermissionKey.canViewCompanyFinancials) ? (
      <Route
        key='financials'
        path={ROUTES.COMPANY_PROFILE_FINANCIALS}
        element={<CompanyProfileFinancials />}
      />
    ) : (
      <Route
        key='financials-blocked'
        path={`${ROUTES.COMPANY_PROFILE_FINANCIALS}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    permSvc.hasPermission(PermissionKey.canViewCaptable) ? (
      <Route key='captable' path={ROUTES.COMPANY_PROFILE_CAP_TABLE} element={<Outlet />}>
        <Route index element={<CompanyProfileCapTable />} />
        {permSvc.hasPermission(PermissionKey.canWriteCaptable) && (
          <Route path={ROUTES.CAP_TABLE_FORM} element={<CaptableFormWrapper />} />
        )}
      </Route>
    ) : (
      <Route
        key='captable-blocked'
        path={`${ROUTES.COMPANY_PROFILE_CAP_TABLE}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    permSvc.hasPermission(PermissionKey.canViewScenarios) ? (
      <Route key='scenarios' path={ROUTES.COMPANY_PROFILE_SCENARIOS} element={<Outlet />}>
        <Route index element={<ScenariosWrapper />} />
        <Route path={ROUTES.COMPANY_PROFILE_SINGLE_SCENARIO} element={<SingleScenario />}>
          <Route path={ROUTES.COMPANY_PROFILE_SCENARIO_SETTINGS} element={<ScenarioForm />} />
          <Route path={ROUTES.COMPANY_PROFILE_SCENARIO_RUN} element={<ScenarioVisualization />} />
          {showWeightedScenarios && (
            <Route
              path={ROUTES.COMPANY_PROFILE_SCENARIO_WEIGHTED}
              element={<WeightedOutcomeVisualization />}
            />
          )}
        </Route>
      </Route>
    ) : (
      <Route
        key='scenarios-blocked'
        path={`${ROUTES.COMPANY_PROFILE_SCENARIOS}/*`}
        element={<RouteNotAllowed />}
      />
    ),
    getDealsRoute(permSvc, <RouteNotAllowed />),
    permSvc.hasPermission(PermissionKey.hasMappingsModule) ? (
      <Route key='mappings' path={ROUTES.COMPANY_PROFILE_MAPPINGS} element={<Mappings />} />
    ) : (
      <Route
        key='mappings-blocked'
        path={`${ROUTES.COMPANY_PROFILE_MAPPINGS}/*`}
        element={<RouteNotAllowed />}
      />
    )
  );

  return routes;
}

function getCompanyProfileRoutes(
  permSvc: PermissionService,
  showPeopleReporting: boolean,
  showWeightedScenarios: boolean,
  isFundAdmin: boolean
): JSX.Element[] {
  const isAuditor = permSvc.hasPermission(PermissionKey.hasAuditorAccess);
  let profileRoutes: JSX.Element[];
  if (isFundAdmin) {
    profileRoutes = getCompanyProfileFundAdminRoutes(permSvc);
  } else if (isAuditor) {
    profileRoutes = getCompanyProfileAuditorRoutes(permSvc, showPeopleReporting);
  } else {
    profileRoutes = getCompanyProfileStandardRoutes(permSvc, showPeopleReporting, showWeightedScenarios);
  }

  return [
    <Route path={ROUTES.COMPANY_PROFILES} key={ROUTES.COMPANY_PROFILES}>
      <Route path={`:id`} element={<CompanyProfiles2 />}>
        {profileRoutes}
      </Route>
    </Route>,
  ];
}

export function RoutedApp() {
  const {
    showSourcing,
    showAllSecurities,
    showFundCapitalAllocation,
    showCompanyMappings,
    showFieldsManager,
    showFormattersConfig,
    showPeopleReporting,
    showFundReserves,
    showGeneralLedger,
    showGlSoiRecon,
    showMetricsRecon,
    useManagedServices,
    useStarbreakerCollectionV2,
    showWeightedScenarios,
    showDocumentHub,
  } = useFlags<MaggieFeatureFlags>();
  const manageAccess = useManageAccessGates();
  const permSvc = PermissionService.get();
  const conditionalRoutes: JSX.Element[] = [];
  const isEntityManagementEnabled = useEntityManagementEnabled();
  const isKpiSourceHierarchyAvailable = useKpiSourceHierarchyAvailable();
  const isSourceRankingAvailable = useSourceRankingAvailable();
  const isRoundCreationAvailable = useRoundCreationAvailable();
  const isBeneficialOwnershipEnabled = useBeneficialOwnershipEnabled();
  const enableFundWaterfall = useAtomValue(clientConfigFieldsAtom)?.Allow_Fund_Waterfall ?? false;
  const usingConsolidatedDataCollection =
    (useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection as boolean) ?? false;
  const isCollectionOnly = useIsCollectionOnly();
  const isFundAdmin = useIsFundAdmin();

  if (permSvc.hasPermission(PermissionKey.hasFundProfilesModule)) {
    conditionalRoutes.push(
      <Route key={ROUTES.FUND_PROFILES} path={`${ROUTES.FUND_PROFILES}/:fundId`} element={<FundProfiles />}>
        <Route index element={<FPIndexRedirect />} />
        <Route path={ROUTES.FP_SUMMARY} element={<FPSummary />} />
        {enableFundWaterfall && <Route path={ROUTES.FP_WATERFALL} element={<FPWaterfall />} />}
        {showFundReserves && <Route path={ROUTES.FP_RESERVES} element={<FundReservesLoader />} />}
        {showFundCapitalAllocation && (
          <Route path={ROUTES.FP_CAPITAL_ALLOCATION} element={<FPAllocationLoader />} />
        )}
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.FUND_PROFILES}/*`} element={<RouteNotAllowed />} key={ROUTES.FUND_PROFILES} />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canViewTransaction)) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.FINANCE2}
        path={ROUTES.FINANCE2}
        element={
          <FrostedGate target='finance' preview={<FinanceModulePreview />}>
            <Finance2 />
          </FrostedGate>
        }
      >
        <Route path={ROUTES.FINANCE2_OVERVIEW} element={<Outlet />}>
          <Route index element={<AllTransactions />} />
          <Route path={ROUTES.FINANCE_DETAILS} element={<Outlet />}>
            <Route path={ROUTES.FINANCE_LEDGER} element={<LedgerGrid />} />
            <Route path={ROUTES.FINANCE_ROUNDS} element={<FinanceRounds />} />
            <Route
              key={ROUTES.FINANCE_TRANSACTIONS_3}
              path={ROUTES.FINANCE_TRANSACTIONS_3}
              element={<Outlet />}
            >
              <Route index element={<TransactionsTable3 />} />
              <Route path={ROUTES.FINANCE_TRANSACTION_HISTORY} element={<TransactionHistory />} />
            </Route>
          </Route>
        </Route>
        {showAllSecurities && (
          <Route
            path={ROUTES.FINANCE_ALL_SECURITIES}
            element={<AllSecurities />}
            key={ROUTES.FINANCE_ALL_SECURITIES}
          />
        )}
        <Route path={ROUTES.FINANCE_RECON} element={<ReconSection />} key={ROUTES.FINANCE_RECON}>
          <Route index element={<RoundReconGrid />} />
          {showMetricsRecon && (
            <Route
              path={ROUTES.FINANCE_RECON_METRICS}
              element={<TransactionReconGrid />}
              key={ROUTES.FINANCE_RECON_METRICS}
            />
          )}
        </Route>
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.FINANCE2}/*`} element={<RouteNotAllowed />} key={ROUTES.FINANCE2} />
    );
  }

  if (isEntityManagementEnabled && !isFundAdmin) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.ENTITIES}
        path={ROUTES.ENTITIES}
        element={
          <FrostedGate target='entities' preview={<EntityManagementModulePreview />}>
            <EntityManagementPage />
          </FrostedGate>
        }
      >
        <Route index element={<Navigate to={ROUTES.ENTITIES_STRUCTURE_CHART} replace />} />
        <Route path={ROUTES.ENTITIES_STRUCTURE_CHART} element={<StructureChartSurface />} />
        <Route path={ROUTES.ENTITIES_REGISTRY} element={<RegistrySurface />} />
        <Route path={ROUTES.ENTITIES_DO_SLATE} element={<DOSlateSurface />} />
        {isBeneficialOwnershipEnabled && (
          <Route path={ROUTES.ENTITIES_BENEFICIAL_OWNERSHIP} element={<BeneficialOwnershipSurface />} />
        )}
        <Route path={ROUTES.ENTITIES_REVIEW_QUEUES} element={<ReviewQueuesSurface />} />
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.ENTITIES}/*`} element={<RouteNotAllowed />} key={ROUTES.ENTITIES} />
    );
  }

  conditionalRoutes.push(
    <Route key={ROUTES.INTEGRATIONS} path={ROUTES.INTEGRATIONS} element={<Outlet />}>
      <Route path={ROUTES.INTEGRATIONS_APPS} element={<IntegrationApps />}>
        <Route path={ROUTES.INTEGRATIONS_APPS_AFFINITY} element={<AffinityConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_AIRTABLE} element={<AirtableSync />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_ALLVUE} element={<AllVueConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_ARKPES} element={<ArkPesConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_ILEVEL} element={<ILevelConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_QUICKBOOKS} element={<QuickBooksConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_STANDARD_METRICS} element={<StandardMetricsConfiguration />} />
        <Route path={ROUTES.INTEGRATIONS_APPS_COBALT} element={<CobaltConfiguration />} />
        <Route
          path={ROUTES.INTEGRATIONS_APPS_CONSOLIDATED_DATA_COLLECTION}
          element={
            permSvc.hasPermission(PermissionKey.canViewClientSettings) ? (
              <ConsolidatedDataCollectionConfiguration />
            ) : (
              <RouteNotAllowed />
            )
          }
        />
        <Route path={ROUTES.INTEGRATIONS_APPS_CARTA} element={<CartaConfiguration />}>
          <Route index element={<Navigate to={ROUTES.COMPANIES} replace />} />
          <Route path={ROUTES.CARTA_FIRMS} element={<CartaFirmsTab />} />
          <Route path={ROUTES.CONFIG_FUNDS} element={<CartaFundsTab />} />
          <Route path={ROUTES.COMPANIES} element={<CartaCompaniesTab />} />
          <Route path={ROUTES.CARTA_HOLDERS} element={<CartaHoldersTab />} />
          <Route path={ROUTES.CARTA_RUNS} element={<CartaRunsTab />} />
        </Route>
        <Route path=':appName' element={<IntegrationDetail />} />
      </Route>
    </Route>
  );

  conditionalRoutes.push(
    ...getDataCollectionModuleRoutes(
      permSvc,
      usingConsolidatedDataCollection,
      useManagedServices,
      useStarbreakerCollectionV2,
      isFundAdmin
    )
  );
  conditionalRoutes.push(
    <Route
      key={ROUTES.BULK_UPLOAD_REPORT}
      path={`${ROUTES.BULK_UPLOAD_REPORT}/:uploadId`}
      element={<BulkUploadReportLoader />}
    />
  );

  if (PermissionService.get().hasPermission(PermissionKey.canViewAppAdminPanel)) {
    conditionalRoutes.push(
      <Route key={ROUTES.ADMIN_PANEL} path={ROUTES.ADMIN_PANEL} element={<AdminPanel />}>
        {PermissionService.get().hasPermission(PermissionKey.canViewClientSettings) && (
          <Route path={ROUTES.APPLICATION_SETTINGS} element={<ConfigClient />} />
        )}
        {showFieldsManager && <Route path={ROUTES.ADMIN_PANEL_FIELD_MANAGER} element={<FieldManager />} />}
        <Route path={ROUTES.ADMIN_PANEL_FIELDS} element={<CustomFields />} />
        <Route path={ROUTES.ADMIN_PANEL_COLUMNS} element={<ConfigColumns />} />
        {showFormattersConfig && PermissionService.get().hasPermission(PermissionKey.customerSuccessAll) && (
          <Route path={ROUTES.ADMIN_PANEL_FORMATTERS} element={<ConfigFormatters />} />
        )}
        {showCompanyMappings && <Route path={ROUTES.ADMIN_PANEL_MAPPINGS} element={<CompanyMappings />} />}
        {manageAccess.section && (
          <>
            <Route
              path={ROUTES.ADMIN_PANEL_MANAGE_ACCESS}
              element={manageAccess.landing ? <ManageAccess /> : <ManageAccessCompanyDataLoader />}
            />
            {manageAccess.landing && manageAccess.companyAccess && (
              <Route
                path={`${ROUTES.ADMIN_PANEL_MANAGE_ACCESS}/${ROUTES.ADMIN_PANEL_MANAGE_ACCESS_COMPANY}`}
                element={<ManageAccessCompanyDataLoader />}
              />
            )}
            {manageAccess.gridColumnVisibility && (
              <Route
                path={`${ROUTES.ADMIN_PANEL_MANAGE_ACCESS}/${ROUTES.ADMIN_PANEL_MANAGE_ACCESS_COLUMN_GRID_DISPLAY}`}
                element={<ManageAccessColumnGridDisplayDataLoader />}
              />
            )}
            {manageAccess.fundAdminAccess && (
              <Route
                path={`${ROUTES.ADMIN_PANEL_MANAGE_ACCESS}/${ROUTES.ADMIN_PANEL_MANAGE_ACCESS_FUND_ADMIN}`}
                element={<ManageAccessFundAdminDataLoader />}
              />
            )}
            {manageAccess.auditorSettings && (
              <Route
                path={`${ROUTES.ADMIN_PANEL_MANAGE_ACCESS}/${ROUTES.ADMIN_PANEL_MANAGE_ACCESS_AUDITOR_SETTINGS}`}
                element={<AuditorDateRangesConfig />}
              />
            )}
          </>
        )}
        {PermissionService.get().hasPermission(PermissionKey.canViewClientSettings) && (
          <>
            <Route path={ROUTES.ADMIN_PANEL_DATA_COLLECTION} element={<DataCollectionConfig />} />
            <Route path={ROUTES.ADMIN_PANEL_AUDITOR_CONFIG} element={<AuditorConfigRedirect />} />
            {(isRoundCreationAvailable || isSourceRankingAvailable) && (
              <Route path={ROUTES.ADMIN_PANEL_ROUND_MANAGEMENT} element={<RoundManagement />}>
                <Route
                  index
                  element={
                    <Navigate
                      to={
                        isRoundCreationAvailable
                          ? ROUTES.ADMIN_PANEL_ROUND_CREATION
                          : ROUTES.ADMIN_PANEL_ROUND_TRANSACTION_SOURCES
                      }
                      replace
                    />
                  }
                />
                {isRoundCreationAvailable && (
                  <Route path={ROUTES.ADMIN_PANEL_ROUND_CREATION} element={<RoundCreationConfig />} />
                )}
                {isSourceRankingAvailable && (
                  <Route
                    path={ROUTES.ADMIN_PANEL_ROUND_TRANSACTION_SOURCES}
                    element={<SourceRankingConfig />}
                  />
                )}
              </Route>
            )}
            {/* Earlier home of the round generation settings, kept for saved links. */}
            {isRoundCreationAvailable && (
              <Route
                path={ROUTES.ADMIN_PANEL_ROUND_CREATION}
                element={
                  <Navigate
                    to={`/${ROUTES.ADMIN_PANEL}/${ROUTES.ADMIN_PANEL_ROUND_MANAGEMENT}/${ROUTES.ADMIN_PANEL_ROUND_CREATION}`}
                    replace
                  />
                }
              />
            )}
          </>
        )}
        <Route path={ROUTES.ADMIN_PANEL_DATA_DISPLAY} element={<DataDisplay />}>
          <Route path={ROUTES.COMPANY_LIST} element={<ListCustomization />} />
          <Route
            path={`${ROUTES.COMPANY_LIST}/${ROUTES.COMPANY_LIST_OVERVIEW}`}
            element={<CompanyListsOverviewConfig />}
          />
          <Route path={ROUTES.COMPANY_PROFILES} element={<ProfileCustomization />} />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.DETAILS_CONFIG}`}
            element={<CompanyDetailsConfig />}
          />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.METRICS_CONFIG}`}
            element={<InvestmentMetricsConfig />}
          />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.COMPANY_SUMMARY_FINANCIAL_SETTINGS}`}
            element={<CompanySummaryFinancialSettingsConfig />}
          />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.COMPANY_SUMMARY_PERFORMANCE_SETTINGS}`}
            element={<CompanySummaryPerformanceSettingsConfig />}
          />
          <Route
            path={`${ROUTES.COMPANY_PROFILES}/${ROUTES.COMPANY_SUMMARY_KPI_CHARTS}`}
            element={<CompanySummaryKPIChartsConfig />}
          />
          <Route path={ROUTES.KPI_DISPLAY} element={<KpiDisplayConfig />} />
          {PermissionService.get().hasPermission(PermissionKey.canViewMetrics) && (
            <Route path={ROUTES.DATA_DISPLAY_PORTFOLIO} element={<PortfolioKpiCardsConfig />} />
          )}
          <Route path={ROUTES.TEAR_SHEETS} element={<TearSheetConfig />} />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_HEADER_CONFIG}`}
            element={<TearSheetHeaderConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_COMPANY_SUMMARY_CONFIG}`}
            element={<TearSheetCompanySummaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_INVESTMENT_SUMMARY_CONFIG}`}
            element={<TearSheetInvestmentSummaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_PERFORMANCE_SUMMARY_CONFIG}`}
            element={<TearSheetPerformanceSummaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_CAPITALIZATION_SUMMARY_CONFIG}`}
            element={<TearSheetCapitalizationSummaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_FINANCING_HISTORY_CONFIG}`}
            element={<TearSheetFinancingHistoryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_INVESTMENT_THESIS_CONFIG}`}
            element={<TearSheetInvestmentThesisConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_PERFORMANCE_COMMENTARY_CONFIG}`}
            element={<TearSheetPerformanceCommentaryConfig />}
          />
          <Route
            path={`${ROUTES.TEAR_SHEETS}/${ROUTES.TEAR_SHEETS_COMPANY_DESCRIPTION_CONFIG}`}
            element={<TearSheetCompanyDescriptionConfig />}
          />
          <Route path={ROUTES.TEAR_SHEETS_V1} element={<TearsheetV1LayoutBuilder />} />
          <Route path={ROUTES.TEAR_SHEETS_V2} element={<TearsheetV2LayoutBuilder />} />
        </Route>
        <Route
          path={ROUTES.ADMIN_PANEL_ORG_PROFILE}
          element={<OrgProfile />}
          key={ROUTES.ADMIN_PANEL_ORG_PROFILE}
        />
        <Route
          path={ROUTES.ADMIN_PANEL_RUBRICS}
          element={<RubricsConfigLoader />}
          key={ROUTES.ADMIN_PANEL_RUBRICS}
        />
        <Route
          path={ROUTES.ADMIN_PANEL_TEARSHEET_GENERATION}
          element={<TearsheetGeneration />}
          key={ROUTES.ADMIN_PANEL_TEARSHEET_GENERATION}
        />
        {permSvc.hasPermission(PermissionKey.canBulkExportData) && (
          <Route
            path={ROUTES.ADMIN_PANEL_TABLE_DOWNLOAD}
            element={<TableDataDownloadLoader />}
            key={ROUTES.ADMIN_PANEL_TABLE_DOWNLOAD}
          />
        )}
        <Route path={ROUTES.CONFIG} key={ROUTES.CONFIG}>
          <Route path={ROUTES.CONFIG_DEAL_TYPES} element={<ConfigDealType />} index />
          <Route path={ROUTES.CONFIG_DEAL_STAGES} element={<ConfigDealStage />} />
          <Route path={ROUTES.CONFIG_PASS_REASONS} element={<ConfigPassReasons />} />
          <Route path={ROUTES.CONFIG_MISS_REASONS} element={<ConfigMissReasons />} />
          <Route path={ROUTES.CONFIG_SECTORS} element={<ConfigSector />} />
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route path={ROUTES.CONFIG_FIELD_AUTOMATION} element={<FieldAutomationConfig />} />
          )}
          <Route path={ROUTES.CONFIG_FUNDS} element={<ConfigFund />} />
          <Route path={ROUTES.CONFIG_KPI} element={<ConfigKPIs />} key={ROUTES.CONFIG_KPI} />
          <Route
            path={ROUTES.CONFIG_KPI_GROUPS}
            element={<ConfigKPIGroups />}
            key={ROUTES.CONFIG_KPI_GROUPS}
          />
          {isKpiSourceHierarchyAvailable && (
            <Route
              path={ROUTES.CONFIG_KPI_SOURCES}
              element={<KpiSourcesConfig />}
              key={ROUTES.CONFIG_KPI_SOURCES}
            />
          )}
          {showGeneralLedger && (
            <Route
              path={ROUTES.GENERAL_LEDGER}
              element={<GeneralLedgerLoader />}
              key={ROUTES.GENERAL_LEDGER}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route
              path={ROUTES.BULK_DEAL_DOCS}
              element={<BulkDealDocsUploadLoader />}
              key={ROUTES.BULK_DEAL_DOCS}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route
              path={ROUTES.UPDATE_DEAL_DOCS_ROUND}
              element={<UpdateDealDocsRoundLoader />}
              key={ROUTES.UPDATE_DEAL_DOCS_ROUND}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route
              path={ROUTES.RESET_REINGESTION}
              element={<ResetReingestion />}
              key={ROUTES.RESET_REINGESTION}
            />
          )}
          <Route path={ROUTES.UPLOAD_STATUS} element={<UploadStatus />} key={ROUTES.UPLOAD_STATUS} />
          {showGlSoiRecon && (
            <Route
              path={ROUTES.ADMIN_PANEL_RECONCILIATION_MONITOR}
              element={<ReconciliationMonitor />}
              key={ROUTES.ADMIN_PANEL_RECONCILIATION_MONITOR}
            />
          )}
          {showGlSoiRecon && (
            <Route
              path={ROUTES.ADMIN_PANEL_ARGUS_PROFILES}
              element={<ArgusProfileInspector />}
              key={ROUTES.ADMIN_PANEL_ARGUS_PROFILES}
            />
          )}
          {showGlSoiRecon && (
            <Route
              path={ROUTES.ADMIN_PANEL_ARGUS_PROFILE_DETAIL}
              element={<ArgusProfileDetail />}
              key={ROUTES.ADMIN_PANEL_ARGUS_PROFILE_DETAIL}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route
              path={ROUTES.ADMIN_PANEL_ATLAS_CONSOLE}
              element={<AtlasConsole />}
              key={ROUTES.ADMIN_PANEL_ATLAS_CONSOLE}
            />
          )}
          {permSvc.hasPermission(PermissionKey.customerSuccessAll) && (
            <Route path={ROUTES.PROCESSING} element={<Processing />} key={ROUTES.PROCESSING} />
          )}
        </Route>
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.ADMIN_PANEL}/*`} element={<RouteNotAllowed />} key={ROUTES.ADMIN_PANEL} />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canViewList) && !isFundAdmin) {
    conditionalRoutes.push(
      <Route key={ROUTES.COMPANIES} path={ROUTES.COMPANIES} element={<Outlet />}>
        <Route index element={<Navigate to={ROUTES.LISTS} replace />} />
        <Route path={ROUTES.LISTS} element={<CompanyListLoader />}>
          <Route path={`:listId`} element={<Companies />} />
        </Route>
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.COMPANIES}/*`} element={<RouteNotAllowed />} key={ROUTES.COMPANIES} />
    );
  }

  if (showSourcing && !isFundAdmin) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.SOURCING}
        path={ROUTES.SOURCING}
        element={
          <FrostedGate target='sourcing' preview={<SourcingModulePreview />}>
            <SourcingPage />
          </FrostedGate>
        }
      >
        <Route index element={<SourcingListsView />} />
        <Route path={`${ROUTES.LISTS}/:listId`} element={<SourcingResultsPage />} />
        <Route path={`${ROUTES.SAVED_SEARCHES}/:savedSearchId`} element={<SourcingResultsPage />} />
        <Route path={`${ROUTES.SOURCING_RESULTS}/:searchId`} element={<SourcingAgentQueryResultPage />} />
        <Route path={`${ROUTES.SOURCING_PROFILE}/:companyId`} element={<SourcingProfilePage />} />
      </Route>
    );
  }

  // Doc Hub delivery needs the company to exist in Starbreaker, which only
  // consolidated data collection syncs.
  if (showDocumentHub && usingConsolidatedDataCollection && !isFundAdmin) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.DOCS}
        path={ROUTES.DOCS}
        element={
          <FrostedGate target='docs' preview={<DocumentHubPreview />}>
            <DocumentHubPage />
          </FrostedGate>
        }
      >
        <Route index element={<DocumentHub />} />
      </Route>
    );
  }

  const showSeparateMprRoute = getShowSeparateMprRoute();
  if (showSeparateMprRoute) {
    conditionalRoutes.push(
      <Route key={ROUTES.MULTIPERIOD_REPORTING} path={ROUTES.MULTIPERIOD_REPORTING} element={<Outlet />}>
        <Route path={`:viewId`} element={<TempMprWrapper />} />
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route
        path={`${ROUTES.MULTIPERIOD_REPORTING}/*`}
        element={<RouteNotAllowed />}
        key={ROUTES.MULTIPERIOD_REPORTING}
      />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canViewMetrics)) {
    conditionalRoutes.push(
      <Route
        key={`${ROUTES.PORTFOLIO}/:viewId`}
        path={`${ROUTES.PORTFOLIO}/:viewId`}
        element={
          <FrostedGate target='portfolio' preview={<PortfolioModulePreview />}>
            <PortfolioOverview />
          </FrostedGate>
        }
      />
    );
    // Base portfolio route - redirects to first available view based on user permissions
    conditionalRoutes.push(
      <Route
        key={ROUTES.PORTFOLIO}
        path={ROUTES.PORTFOLIO}
        element={
          <FrostedGate target='portfolio' preview={<PortfolioModulePreview />}>
            <PortfolioRedirect />
          </FrostedGate>
        }
      />
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.PORTFOLIO}/*`} element={<RouteNotAllowed />} key={ROUTES.PORTFOLIO} />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canReadDataCollection) && !isFundAdmin) {
    conditionalRoutes.push(
      <Route key={ROUTES.KPI_REQUEST_REVIEW} path={ROUTES.KPI_REQUEST_REVIEW} element={<KPIReviewerView />} />
    );
  } else {
    conditionalRoutes.push(
      <Route
        path={`${ROUTES.KPI_REQUEST_REVIEW}/*`}
        element={<RouteNotAllowed />}
        key={ROUTES.KPI_REQUEST_REVIEW}
      />
    );
  }

  if (permSvc.hasPermission(PermissionKey.canViewDeal) && !isFundAdmin) {
    conditionalRoutes.push(
      <Route
        key={ROUTES.DEAL_FLOW}
        path={ROUTES.DEAL_FLOW}
        element={
          <FrostedGate target='pipeline' preview={<PipelineModulePreview />}>
            <Outlet />
          </FrostedGate>
        }
      >
        <Route path={ROUTES.DEAL_ANALYTICS} element={<DealAnalytics />}>
          <Route path={ROUTES.DEAL_ANALYTICS_KPI} element={<DAOverview />} />
          <Route path={ROUTES.DEAL_ANALYTICS_DURATION_DETAILS} element={<DealAnalyticsDuration />} />
        </Route>

        <Route path={`${ROUTES.DEALS}?`} element={<DealFlowPreLoader />}>
          <Route path={`:dealCategory`} element={<DealsView />} />
        </Route>
      </Route>
    );
  } else {
    conditionalRoutes.push(
      <Route path={`${ROUTES.DEAL_FLOW}/*`} element={<RouteNotAllowed />} key={ROUTES.DEAL_FLOW} />
    );
  }

  if (!permSvc.hasPermission(PermissionKey.baseline)) {
    return (
      <Routes>
        <Route path='*' element={<RouteNotAllowed />} />
      </Routes>
    );
  }

  if (PermissionService.get().hasPersona(PersonaKey.respondent)) {
    return (
      <Routes>
        <Route path={'/'} element={<RouteWrapper />}>
          <Route
            key={ROUTES.KPI_THANK_YOU_SCREEN}
            path={ROUTES.KPI_THANK_YOU_SCREEN}
            element={<KPIThankYouScreen />}
          />
          <Route
            key={ROUTES.KPI_PENDING_REQUESTS}
            path={ROUTES.KPI_PENDING_REQUESTS}
            element={<KPIRecipientView3 />}
          />
          <Route path='*' element={<RouteNotAllowed />} />
        </Route>
      </Routes>
    );
  }

  if (permSvc.hasPersona(PersonaKey.managedServices)) {
    if (!permSvc.hasPermission(PermissionKey.canReadDataCollection)) {
      return (
        <Routes>
          <Route path='*' element={<RouteNotAllowed />} />
        </Routes>
      );
    }
    return (
      <Routes>
        <Route path={'/'} element={<RouteWrapper />}>
          <Route key={ROUTES.KPI} path={ROUTES.KPI} element={<KpiModuleGate variant='managedServices' />}>
            <Route path={ROUTES.KPI_RESPONSES} element={<KPIResponses />}>
              <Route index element={<Navigate to={ROUTES.KPI_RESPONSES_MS_PENDING} replace />} />
              <Route
                path={ROUTES.KPI_RESPONSES_MS_PENDING}
                element={<KPIResponsesGrid status={KPIRequestStatus.ManagedServiceSubmitted} />}
              />
              <Route
                path={ROUTES.KPI_RESPONSES_MS_IN_PROGRESS}
                element={<KPIResponsesGrid status={KPIRequestStatus.ManagedServiceInProgress} />}
              />
              <Route
                path={ROUTES.KPI_RESPONSES_MS_REVIEWED}
                element={<KPIResponsesGrid status={KPIRequestStatus.ManagedServiceFinished} />}
              />
              <Route
                path={ROUTES.KPI_RESPONSES_REJECTED}
                element={<KPIResponsesGrid status={KPIRequestStatus.Rejected} />}
              />
            </Route>
          </Route>
          <Route
            key={ROUTES.KPI_REQUESTS_RESPONSES_HOME}
            path={ROUTES.KPI_REQUESTS_RESPONSES_HOME}
            element={<KPIRequestResponseHome />}
          />
          <Route
            key={ROUTES.KPI_REQUEST_REVIEW}
            path={ROUTES.KPI_REQUEST_REVIEW}
            element={<ManagedServiceView />}
          />
          <Route path='*' element={<RouteNotAllowed />} />
        </Route>
      </Routes>
    );
  }

  if (isCollectionOnly) {
    return (
      <Routes>
        <Route path={'/signout'} element={<Navigate replace to='/' />} />
        <Route path={'/'} element={<RouteWrapper />}>
          <Route index element={<Navigate to={ROUTES.KPI} replace />} />
          <Route path={ROUTES.CHATBOT} element={<ChatBot />} />

          {/* Data Collection Module */}
          {getDataCollectionModuleRoutes(
            permSvc,
            usingConsolidatedDataCollection,
            useManagedServices,
            useStarbreakerCollectionV2,
            isFundAdmin
          )}
          {/* These static paths outrank the helper's forbidden `kpis/*`, so they need the same fund admin guard */}
          {permSvc.hasPermission(PermissionKey.canReadDataCollection) && !isFundAdmin && (
            <>
              <Route
                key={ROUTES.KPI_REQUESTS_RESPONSES_HOME}
                path={ROUTES.KPI_REQUESTS_RESPONSES_HOME}
                element={<KPIRequestResponseHome />}
              />
              <Route
                key={ROUTES.KPI_REQUEST_REVIEW}
                path={ROUTES.KPI_REQUEST_REVIEW}
                element={<KPIReviewerView />}
              />
            </>
          )}

          {/* Company Profiles */}
          {getCompanyProfileRoutes(permSvc, showPeopleReporting, showWeightedScenarios, isFundAdmin)}

          <Route path='*' element={<CollectionOnlyRouteNotAllowed />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path={'/signout'} element={<Navigate replace to='/' />} />

      <Route path={'/'} element={<RouteWrapper />}>
        <Route index element={<IndexComponent />} />
        <Route path={ROUTES.CHATBOT} element={<ChatBot />} />
        <Route path={ROUTES.EXCEL_LOGIN} element={<LoginSuccess />} />
        <Route path={ROUTES.COMPANIES_OVERVIEW} element={<CompaniesOverview />} />
        {isNotProduction() && <Route path={ROUTES.UI} element={<UI />} />}
        {isNotProduction() && <Route path={'cap'} element={<UI />} />}

        {getCompanyProfileRoutes(permSvc, showPeopleReporting, showWeightedScenarios, isFundAdmin)}
        {conditionalRoutes}
        <Route path='*' element={<Navigate to='/' />} key='*' />
      </Route>
    </Routes>
  );
}

function IndexComponent() {
  const permSvc = PermissionService.get();
  const isCollectionOnly = useIsCollectionOnly();

  if (isMobileDevice(true)) {
    return <Navigate to={ROUTES.CHATBOT} replace />;
  }

  if (isCollectionOnly) {
    return <Navigate to={ROUTES.KPI} replace />;
  }

  if (permSvc.hasPersona(PersonaKey.limitedProfile)) {
    return <Navigate to={ROUTES.COMPANIES_OVERVIEW} replace />;
  }

  if (permSvc.hasPermission(PermissionKey.canViewMetrics)) {
    return <Navigate to={ROUTES.PORTFOLIO} replace />;
  }
  if (permSvc.hasPermission(PermissionKey.canViewList)) {
    return <Companies />;
  }
  return <div />;
}

function RouteNotAllowed() {
  return (
    <ForbiddenFallback
      error={new Error(`Forbidden: missing permissions; route: ${window.location.pathname};`)}
    />
  );
}

function CollectionOnlyRouteNotAllowed() {
  return (
    <ForbiddenError
      error={new Error(`Collection-only access restriction; route: ${window.location.pathname};`)}
      config={CollectionOnlyForbiddenMessage}
    />
  );
}

// What a click into each section lands on, most used first. The boot preloads the current section's
// entries and the idle warm-up fetches the rest ahead of the first navigation.
const SECTION_LANDINGS: Array<[string, Array<{ preload: Preload }>]> = [
  [ROUTES.PORTFOLIO, [PortfolioOverview, PortfolioRedirect]],
  [ROUTES.FUND_PROFILES, [FundProfiles, FPSummary]],
  [ROUTES.COMPANY_PROFILES, [CompanyProfiles2, CompanySummary2]],
  [ROUTES.FINANCE2, [Finance2]],
  [ROUTES.ENTITIES, [EntityManagementPage, StructureChartSurface]],
  [ROUTES.KPI, [KpiModuleGate, KPICompanies, AllCompanies]],
  [ROUTES.DEAL_FLOW, [DealFlowPreLoader]],
  [ROUTES.COMPANIES, [CompanyListLoader, Companies]],
  [ROUTES.ADMIN_PANEL, [AdminPanel]],
  [ROUTES.INTEGRATIONS, [IntegrationApps]],
  [ROUTES.SOURCING, [SourcingPage]],
  [ROUTES.MULTIPERIOD_REPORTING, [TempMprWrapper]],
];

/** Starts downloading the page modules the landing route renders. The root redirects to Portfolio. */
export function preloadPagesForPath(pathname: string) {
  const segment = pathname.split('/')[1] || ROUTES.PORTFOLIO;
  const landing = SECTION_LANDINGS.find(([route]) => route === segment)?.[1] ?? [];
  // The render that needs the page reports a failure, so the head start must not add an
  // unhandled rejection.
  landing.forEach((page) => void page.preload().catch(() => undefined));
}

/** Every section's landing modules in priority order, for the warm-up after first render. */
export function sectionPreloads(): Preload[] {
  return SECTION_LANDINGS.flatMap(([, pages]) => pages.map((page) => page.preload));
}
